import { useState } from 'react';
import { type CabinModerationConfig } from '@/mocks/adminSettingsCabina';

interface Props {
  config: CabinModerationConfig;
  onChange: (config: CabinModerationConfig) => void;
  onSave: () => void;
  saving: boolean;
}

export default function CabinModerationSection({ config, onChange, onSave, saving }: Props) {
  const [newWord, setNewWord] = useState('');

  const toggle = (key: keyof CabinModerationConfig) => {
    if (typeof config[key] === 'boolean') {
      onChange({ ...config, [key]: !config[key] });
    }
  };

  const updateNumber = (key: 'maxFloodMensajes' | 'advertenciasAntesBloqueo' | 'tiempoCastigoMinutos', value: number) => {
    onChange({ ...config, [key]: value });
  };

  const addWord = () => {
    const trimmed = newWord.trim().toLowerCase();
    if (trimmed && !config.palabrasBloqueadas.includes(trimmed)) {
      onChange({ ...config, palabrasBloqueadas: [...config.palabrasBloqueadas, trimmed] });
      setNewWord('');
    }
  };

  const removeWord = (word: string) => {
    onChange({ ...config, palabrasBloqueadas: config.palabrasBloqueadas.filter((w) => w !== word) });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addWord();
    }
  };

  return (
    <div className="space-y-6">
      {/* Blocked words */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <div className="w-7 h-7 rounded-lg bg-red-100 flex items-center justify-center">
            <i className="ri-file-text-line text-red-500 text-sm"></i>
          </div>
          <h4 className="text-sm font-semibold text-foreground-900">Lista de Palabras Prohibidas</h4>
          <span className="ml-auto text-foreground-400 text-[10px]">{config.palabrasBloqueadas.length} palabras</span>
        </div>

        <div className="flex gap-2 mb-3">
          <input
            type="text"
            value={newWord}
            onChange={(e) => setNewWord(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Escribí una palabra y presioná Enter..."
            className="flex-1 h-10 px-3.5 rounded-xl border border-background-200 bg-background-50 text-foreground-900 text-xs placeholder:text-foreground-300 focus:outline-none focus:border-red-300 transition-colors"
            maxLength={50}
          />
          <button
            onClick={addWord}
            disabled={!newWord.trim()}
            className="flex items-center gap-1.5 px-4 h-10 rounded-xl bg-red-500 text-background-50 text-xs font-semibold hover:bg-red-600 transition-colors cursor-pointer disabled:opacity-40 whitespace-nowrap"
          >
            <i className="ri-add-line text-sm"></i>
            Agregar
          </button>
        </div>

        {config.palabrasBloqueadas.length > 0 ? (
          <div className="flex flex-wrap gap-1.5">
            {config.palabrasBloqueadas.map((word) => (
              <span
                key={word}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-background-200 text-foreground-700 text-[10px] font-medium"
              >
                {word}
                <button
                  onClick={() => removeWord(word)}
                  className="w-4 h-4 rounded-full flex items-center justify-center hover:bg-red-100 hover:text-red-500 transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-[10px]"></i>
                </button>
              </span>
            ))}
          </div>
        ) : (
          <p className="text-foreground-300 text-[10px] italic">No hay palabras en la lista.</p>
        )}
      </div>

      {/* Divider */}
      <div className="border-t border-background-200"></div>

      {/* Block toggles */}
      <div>
        <h4 className="text-sm font-semibold text-foreground-900 mb-3">Controles de Moderación</h4>

        <div className="space-y-2.5">
          {[
            {
              key: 'bloquearSpam' as const,
              label: 'Bloquear Spam',
              icon: 'ri-spam-2-line',
              desc: 'Detecta y bloquea mensajes repetitivos y no solicitados.',
              color: 'bg-orange-500',
            },
            {
              key: 'bloquearLinks' as const,
              label: 'Bloquear Links',
              icon: 'ri-link-unlink',
              desc: 'Impide que los usuarios publiquen enlaces en el chat.',
              color: 'bg-rose-500',
            },
            {
              key: 'bloquearFlood' as const,
              label: 'Bloquear Flood',
              icon: 'ri-flood-line',
              desc: 'Limita la cantidad de mensajes que un usuario puede enviar por minuto.',
              color: 'bg-amber-500',
            },
          ].map((item) => (
            <div
              key={item.key}
              className={`flex items-start gap-3 p-3 rounded-xl border transition-all ${
                config[item.key]
                  ? 'border-background-200 bg-background-50'
                  : 'border-background-200 bg-background-100/50 opacity-60'
              }`}
            >
              <div className={`w-8 h-8 rounded-lg ${item.color} flex items-center justify-center shrink-0 ${!config[item.key] && 'opacity-40'}`}>
                <i className={`${item.icon} text-background-50 text-sm`}></i>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold text-foreground-800">{item.label}</p>
                  <button
                    onClick={() => toggle(item.key)}
                    className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer shrink-0 ${
                      config[item.key] ? 'bg-secondary-500' : 'bg-background-300'
                    }`}
                  >
                    <span
                      className={`absolute top-0.5 w-4 h-4 rounded-full bg-background-50 transition-all ${
                        config[item.key] ? 'left-[20px]' : 'left-0.5'
                      }`}
                    ></span>
                  </button>
                </div>
                <p className="text-foreground-400 text-[9px] leading-relaxed mt-0.5">{item.desc}</p>

                {/* Flood config */}
                {item.key === 'bloquearFlood' && config.bloquearFlood && (
                  <div className="mt-2 flex items-center gap-3">
                    <label className="text-foreground-500 text-[10px]">Máx. mensajes por minuto:</label>
                    <select
                      value={config.maxFloodMensajes}
                      onChange={(e) => updateNumber('maxFloodMensajes', Number(e.target.value))}
                      className="h-8 px-2.5 rounded-lg border border-background-200 bg-background-50 text-foreground-900 text-[10px] font-medium focus:outline-none focus:border-amber-300 cursor-pointer"
                    >
                      {[3, 5, 8, 10, 15, 20].map((n) => (
                        <option key={n} value={n}>{n}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Divider */}
      <div className="border-t border-background-200"></div>

      {/* Auto-block */}
      <div>
        <h4 className="text-sm font-semibold text-foreground-900 mb-3">Bloqueo Automático</h4>

        <div className="flex items-start gap-3 p-3 rounded-xl border border-background-200 bg-background-50">
          <div className="w-8 h-8 rounded-lg bg-red-500 flex items-center justify-center shrink-0">
            <i className="ri-shield-check-line text-background-50 text-sm"></i>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold text-foreground-800">Activar Bloqueo Automático</p>
              <button
                onClick={() => toggle('bloqueoAutomatico')}
                className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer shrink-0 ${
                  config.bloqueoAutomatico ? 'bg-secondary-500' : 'bg-background-300'
                }`}
              >
                <span
                  className={`absolute top-0.5 w-4 h-4 rounded-full bg-background-50 transition-all ${
                    config.bloqueoAutomatico ? 'left-[20px]' : 'left-0.5'
                  }`}
                ></span>
              </button>
            </div>
            <p className="text-foreground-400 text-[9px] leading-relaxed mt-0.5">
              El sistema bloquea automáticamente a los usuarios que acumulen advertencias.
            </p>

            {config.bloqueoAutomatico && (
              <div className="grid grid-cols-2 gap-3 mt-3">
                <div>
                  <label className="text-foreground-500 text-[10px] block mb-1">Advertencias antes del bloqueo</label>
                  <select
                    value={config.advertenciasAntesBloqueo}
                    onChange={(e) => updateNumber('advertenciasAntesBloqueo', Number(e.target.value))}
                    className="w-full h-9 px-2.5 rounded-lg border border-background-200 bg-background-50 text-foreground-900 text-xs font-medium focus:outline-none focus:border-red-300 cursor-pointer"
                  >
                    {[1, 2, 3, 5, 7, 10].map((n) => (
                      <option key={n} value={n}>{n}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-foreground-500 text-[10px] block mb-1">Tiempo de castigo</label>
                  <select
                    value={config.tiempoCastigoMinutos}
                    onChange={(e) => updateNumber('tiempoCastigoMinutos', Number(e.target.value))}
                    className="w-full h-9 px-2.5 rounded-lg border border-background-200 bg-background-50 text-foreground-900 text-xs font-medium focus:outline-none focus:border-red-300 cursor-pointer"
                  >
                    {[
                      { value: 15, label: '15 minutos' },
                      { value: 30, label: '30 minutos' },
                      { value: 60, label: '1 hora' },
                      { value: 180, label: '3 horas' },
                      { value: 360, label: '6 horas' },
                      { value: 720, label: '12 horas' },
                      { value: 1440, label: '24 horas' },
                    ].map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Save */}
      <div className="flex items-center justify-between pt-2 border-t border-background-200">
        <p className="text-foreground-400 text-[10px]">
          {config.palabrasBloqueadas.length} palabras · {[config.bloquearSpam, config.bloquearLinks, config.bloquearFlood].filter(Boolean).length} filtros activos · {config.bloqueoAutomatico ? 'Auto-bloqueo ON' : 'Auto-bloqueo OFF'}
        </p>
        <button
          onClick={onSave}
          disabled={saving}
          className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-foreground-950 text-background-50 text-xs font-semibold hover:bg-foreground-800 transition-all cursor-pointer disabled:opacity-60 whitespace-nowrap"
        >
          {saving ? (
            <>
              <i className="ri-loader-4-line animate-spin text-sm"></i>
              Guardando...
            </>
          ) : (
            <>
              <i className="ri-check-line text-sm"></i>
              Guardar Cambios
            </>
          )}
        </button>
      </div>
    </div>
  );
}